import React, { useState } from 'react';
import { Card, Badge } from '../../design-system';
import CheckImageLightbox from '../ui/CheckImageLightbox';
import PDFSidePanel from '../ui/pdf-side-panel';
import { formatCurrency, formatDate } from '../../utils/formatters';
import styles from './CheckImageReviewPanel.module.css';

/**
 * HIL-assist center panel: shows ONE unmatched check/deposit-slip crop the agent
 * presigned (render_check_image tool) alongside WHAT WAS READ from it, so the
 * reviewer can confirm or correct the read and tell Livi which transaction it
 * belongs to (agent-assisted resolution via the resolve_check_image tool).
 * Display-only — the reviewer acts through chat. The presigned imageUrl is
 * short-lived and the S3 key never reaches the client.
 *
 * When the scan's statement PDF is available (pdfBucket/pdfKey), the crop + "what
 * was read" become the LEFT pane of the shared PDFSidePanel, with a readable,
 * zoomable statement PDF opened to the check's page on the right — so the reviewer
 * can cross-reference the slip against its ledger line (Trello #800). Without those
 * refs the panel renders crop-only, exactly as before.
 */
export default function CheckImageReviewPanel({ imageUrl, docKind, page, extracted = {}, pdfBucket = null, pdfKey = null, documentName = null }) {
  const [zoom, setZoom] = useState(false);
  const isDeposit = docKind === 'deposit_slip';
  const kindLabel = isDeposit ? 'Deposit Slip' : 'Check';
  const whoLabel = isDeposit ? 'Depositor' : 'Payee';
  const whoValue = isDeposit ? extracted.depositor : extracted.check_payee;

  // "What was read" rows, doc-kind-aware. Amount and date always show (the amount
  // is the usual culprit); check number + address only for checks.
  const rows = [
    { label: 'Amount', value: extracted.amount != null ? formatCurrency(extracted.amount) : null },
    { label: 'Date', value: extracted.date ? formatDate(extracted.date) : null },
    !isDeposit && { label: 'Check #', value: extracted.check_number },
    { label: whoLabel, value: whoValue },
    !isDeposit && { label: 'Address', value: extracted.check_payee_address },
    { label: 'Memo', value: extracted.check_memo },
  ].filter(Boolean);

  // The crop + "what was read" become the left pane; PDFSidePanel adds the
  // readable statement PDF on the right when pdfBucket/pdfKey are present, and
  // renders this content full-width when they aren't.
  const reviewContent = (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <span className={styles.title}>Unmatched {kindLabel} Image</span>
          {page != null && <span className={styles.caption}>from page {page}</span>}
        </div>
        <Badge variant="warning" size="sm">Needs review</Badge>
      </div>

      {imageUrl && (
        <button
          type="button"
          className={styles.imageButton}
          onClick={() => setZoom(true)}
          aria-label={`Enlarge ${kindLabel.toLowerCase()} image`}
        >
          <img className={styles.image} src={imageUrl} alt={`Scanned ${kindLabel.toLowerCase()}`} />
        </button>
      )}

      <Card padding="md">
        <div className={styles.readTitle}>What was read</div>
        <dl className={styles.readList}>
          {rows.map((r) => (
            <div key={r.label} className={styles.readRow}>
              <dt className={styles.readLabel}>{r.label}</dt>
              <dd className={styles.readValue}>
                {r.value || <span className={styles.empty}>—</span>}
              </dd>
            </div>
          ))}
        </dl>
        <p className={styles.hint}>
          This {kindLabel.toLowerCase()} couldn’t be matched to a transaction automatically.
          Confirm or correct what was read with Livi, then tell Livi which transaction it belongs to.
        </p>
      </Card>

      <CheckImageLightbox
        open={zoom}
        onClose={() => setZoom(false)}
        imageUrl={imageUrl}
        docKind={docKind}
      />
    </div>
  );

  return (
    <PDFSidePanel
      leftChild={reviewContent}
      pdfBucket={pdfBucket}
      pdfKey={pdfKey}
      documentName={documentName}
      displayPage={page || 1}
    />
  );
}
